import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-button-primary text-white hover:bg-button-primary-hover active:bg-button-primary-active disabled:bg-button-primary/40',
  secondary: 'bg-white text-text-primary border border-border hover:bg-bg active:bg-border/40',
  ghost: 'bg-transparent text-text-secondary hover:bg-bg hover:text-text-primary',
  danger: 'bg-danger text-white hover:bg-danger/90 active:bg-danger/80 disabled:bg-danger/40',
};

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'h-8 px-3 text-[14px] gap-1.5',
  md: 'h-10 px-4 text-[15px] gap-2',
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  loading = false,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center rounded-button font-medium
        transition-colors duration-150 ease-out disabled:cursor-not-allowed
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1
        ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]}`}
      {...rest}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
}
